import React, { createContext, ReactNode, useState } from 'react';
import { ICrumb } from '../interfaces/ICrumb';
import {
  IDeclaracaoHastaPublica,
  IDeclaracaoPrimeiroAdquirente,
  IDeclaracaoTitularOuArrendatario,
} from '../interfaces/IDeclaracoes';
import { IMensagem } from '../interfaces/IMensage';
import { EMetodoReceitaBrutaCalculada } from '../enums/EMetodoBaseCalculo';
import { IErrosValidacaoDeclaracao } from '../interfaces/IErroValidacaoDeclaracao';

export interface DiefCfemContextType {
  crumbs: ICrumb[];
  setCrumbs: React.Dispatch<React.SetStateAction<ICrumb[]>>;
  mensagens: IMensagem[];
  setMensagens: React.Dispatch<React.SetStateAction<IMensagem[]>>;
  errosValidacaoDeclaracao: IErrosValidacaoDeclaracao | undefined;
  setErrosValidacaoDeclaracao: React.Dispatch<React.SetStateAction<IErrosValidacaoDeclaracao | undefined>>;
  declaracaoPrimeiroAdquirente: IDeclaracaoPrimeiroAdquirente;
  setDeclaracaoPrimeiroAdquirente: React.Dispatch<React.SetStateAction<IDeclaracaoPrimeiroAdquirente>>;
  declaracaoHastaPublica: IDeclaracaoHastaPublica;
  setDeclaracaoHastaPublica: React.Dispatch<React.SetStateAction<IDeclaracaoHastaPublica>>;
  declaracaoTitularArrendatario: IDeclaracaoTitularOuArrendatario;
  setDeclaracaoTitularArrendatario: React.Dispatch<React.SetStateAction<IDeclaracaoTitularOuArrendatario>>;
  idOperacaoEditar: string;
  setIdOperacaoEditar: React.Dispatch<React.SetStateAction<string>>;
  idBalneario: number;
  setIdBalneario: React.Dispatch<React.SetStateAction<number>>;
  idBemTerceiro: number;
  setIdBemTerceiro: React.Dispatch<React.SetStateAction<number>>;
  idNCMBemTerceiro: number;
  setIdNCMBemTerceiro: React.Dispatch<React.SetStateAction<number>>;
  idReceitaBruta: number;
  setIdReceitaBruta: React.Dispatch<React.SetStateAction<number>>;
  idReceitaBrutaNCM: number;
  setIdReceitaBrutaNCM: React.Dispatch<React.SetStateAction<number>>;
  idReceitaBrutaCalculada: number;
  setIdReceitaBrutaCalculada: React.Dispatch<React.SetStateAction<number>>;
  idReceitaBrutaCalculadaNCM: number;
  setIdReceitaBrutaCalculadaNCM: React.Dispatch<React.SetStateAction<number>>;
  receitaBrutaCalculadaMetodo: EMetodoReceitaBrutaCalculada;
  setReceitaBrutaCalculadaMetodo: React.Dispatch<React.SetStateAction<EMetodoReceitaBrutaCalculada>>;
  idVendaNacional: number;
  setIdVendaNacional: React.Dispatch<React.SetStateAction<number>>;
  idVendaNacionalNCM: number;
  setIdVendaNacionalNCM: React.Dispatch<React.SetStateAction<number>>;
  idReceitaExportacoes: number;
  setIdReceitaExportacoes: React.Dispatch<React.SetStateAction<number>>;
  idReceitaExportacoesNCM: number;
  setIdReceitaExportacoesNCM: React.Dispatch<React.SetStateAction<number>>;
  hasOperacaoVinculadaReceitaExportacoes: boolean;
  setHasOperacaoVinculadaReceitaExportacoes: React.Dispatch<React.SetStateAction<boolean>>;
  nrProcessoMinerarioEmPreenchimento: string;
  setNrProcessoMinerarioEmPreenchimento: React.Dispatch<React.SetStateAction<string>>;
}

// Crie o contexto com um valor inicial
export const DiefCfemContext = createContext<DiefCfemContextType | undefined>(undefined);

// Defina o tipo para as propriedades do GlobalProvider
interface GlobalProviderProps {
  children: ReactNode;
}

export const declaracaoPrimeiroAdquirenteInicial: IDeclaracaoPrimeiroAdquirente = {
  id: '',
  statusDeclaracao: 0,
  tipoDeclarante: 1,
  declarante: {
    nomeRazaoSocial: '',
    cpfCnpj: '',
  },
  exercicioMes: 0,
  tipoDeclaracao: 0,
  exercicioAno: 0,
  regimeTributacao: {
    id: -1,
    nome: '',
  },
  filiais: [],
};

export const declaracaoHastaPublicaInicial: IDeclaracaoHastaPublica = {
  id: '',
  statusDeclaracao: 0,
  tipoDeclarante: 2,
  declarante: {
    nomeRazaoSocial: '',
    cpfCnpj: '',
  },
  exercicioMes: 0,
  tipoDeclaracao: 0,
  exercicioAno: 0,
  regimeTributacao: {
    id: -1,
    nome: '',
  },
};

export const declaracaoTitularArrendatarioInicial: IDeclaracaoTitularOuArrendatario = {
  id: '',
  statusDeclaracao: 0,
  tipoDeclarante: 0,
  declarante: {
    nomeRazaoSocial: '',
    cpfCnpj: '',
  },
  exercicioMes: 0,
  tipoDeclaracao: 0,
  exercicioAno: 0,
  regimeTributacao: {
    id: -1,
    nome: '',
  },
};

export const DiefCfemProvider = ({ children }: GlobalProviderProps) => {
  const [crumbs, setCrumbs] = useState<ICrumb[]>([]);
  const [mensagens, setMensagens] = useState<IMensagem[]>([]);
  const [errosValidacaoDeclaracao, setErrosValidacaoDeclaracao] = useState<IErrosValidacaoDeclaracao>();
  const [declaracaoPrimeiroAdquirente, setDeclaracaoPrimeiroAdquirente] = useState<IDeclaracaoPrimeiroAdquirente>(
    declaracaoPrimeiroAdquirenteInicial
  );
  const [declaracaoHastaPublica, setDeclaracaoHastaPublica] =
    useState<IDeclaracaoHastaPublica>(declaracaoHastaPublicaInicial);
  const [declaracaoTitularArrendatario, setDeclaracaoTitularArrendatario] = useState<IDeclaracaoTitularOuArrendatario>(
    declaracaoTitularArrendatarioInicial
  );

  const [idBalneario, setIdBalneario] = useState<number>(0);

  const [idOperacaoEditar, setIdOperacaoEditar] = useState<string>('');

  const [idBemTerceiro, setIdBemTerceiro] = useState<number>(-1);
  const [idNCMBemTerceiro, setIdNCMBemTerceiro] = useState<number>(-1);

  const [idReceitaBruta, setIdReceitaBruta] = useState<number>(-1);
  const [idReceitaBrutaNCM, setIdReceitaBrutaNCM] = useState<number>(-1);

  const [idReceitaBrutaCalculada, setIdReceitaBrutaCalculada] = useState<number>(-1);
  const [idReceitaBrutaCalculadaNCM, setIdReceitaBrutaCalculadaNCM] = useState<number>(-1);
  const [receitaBrutaCalculadaMetodo, setReceitaBrutaCalculadaMetodo] = useState<EMetodoReceitaBrutaCalculada>(
    EMetodoReceitaBrutaCalculada.PRECO_CORRENTE
  );
  const [idVendaNacional, setIdVendaNacional] = useState<number>(-1);
  const [idVendaNacionalNCM, setIdVendaNacionalNCM] = useState<number>(-1);

  const [idReceitaExportacoes, setIdReceitaExportacoes] = useState<number>(-1);
  const [idReceitaExportacoesNCM, setIdReceitaExportacoesNCM] = useState<number>(-1);
  const [hasOperacaoVinculadaReceitaExportacoes, setHasOperacaoVinculadaReceitaExportacoes] = useState<boolean>(false);
  const [nrProcessoMinerarioEmPreenchimento, setNrProcessoMinerarioEmPreenchimento] = useState<string>('');

  return (
    <DiefCfemContext.Provider
      value={{
        crumbs,
        setCrumbs,
        mensagens,
        setMensagens,
        errosValidacaoDeclaracao,
        setErrosValidacaoDeclaracao,
        declaracaoPrimeiroAdquirente,
        setDeclaracaoPrimeiroAdquirente,
        declaracaoHastaPublica,
        setDeclaracaoHastaPublica,
        declaracaoTitularArrendatario,
        setDeclaracaoTitularArrendatario,
        idOperacaoEditar,
        setIdOperacaoEditar,
        idBalneario,
        setIdBalneario,
        idBemTerceiro,
        setIdBemTerceiro,
        idNCMBemTerceiro,
        setIdNCMBemTerceiro,
        idReceitaBruta,
        setIdReceitaBruta,
        idReceitaBrutaNCM,
        setIdReceitaBrutaNCM,
        idReceitaBrutaCalculada,
        setIdReceitaBrutaCalculada,
        idReceitaBrutaCalculadaNCM,
        setIdReceitaBrutaCalculadaNCM,
        receitaBrutaCalculadaMetodo,
        setReceitaBrutaCalculadaMetodo,
        idVendaNacional,
        setIdVendaNacional,
        idVendaNacionalNCM,
        setIdVendaNacionalNCM,
        idReceitaExportacoes,
        setIdReceitaExportacoes,
        idReceitaExportacoesNCM,
        setIdReceitaExportacoesNCM,
        hasOperacaoVinculadaReceitaExportacoes,
        setHasOperacaoVinculadaReceitaExportacoes,
        nrProcessoMinerarioEmPreenchimento,
        setNrProcessoMinerarioEmPreenchimento,
      }}
    >
      {children}
    </DiefCfemContext.Provider>
  );
};
